/**
 * Words of shared mailboxes (PRD 04 §2.3, ADR 0005 §2) [T]: addresses that a team, a department
 * or a system reads, not one identifiable person. The HR console never gives the HR role to one
 * of them, because whoever reads the mailbox would act as HR and the audit trail could not say
 * who. The operator tool is exempt: it is Eklevo's way out for exceptional cases. Written without
 * accents, dots, hyphens or underscores: `recursos.humanos`, `recursos-humanos` and
 * `recursoshumanos` are the same mailbox.
 */
const GENERIC_LOCAL_PARTS: ReadonlySet<string> = new Set([
  // People and HR.
  'rrhh',
  'hhrr',
  'hr',
  'rh',
  'recursoshumanos',
  'recursoshumans',
  'humanresources',
  'personal',
  'people',
  'peopleops',
  'personas',
  'persones',
  'empleados',
  'empleats',
  'employees',
  'plantilla',
  'talento',
  'talent',
  'talentohumano',
  'seleccion',
  'seleccio',
  'recruiting',
  'recruitment',
  'jobs',
  'cv',
  'curriculum',
  'candidatos',
  'candidats',
  'empleo',
  'feina',
  'careers',
  'nomina',
  'nominas',
  'nomines',
  'payroll',
  'formacion',
  'formacio',
  'training',
  // The organization as a whole.
  'info',
  'informacion',
  'informacio',
  'information',
  'contact',
  'contacto',
  'contacte',
  'hola',
  'hello',
  'office',
  'oficina',
  'general',
  'mail',
  'email',
  'correo',
  'correu',
  'admin',
  'administracion',
  'administracio',
  'administration',
  'administrador',
  'administrator',
  'admon',
  'direccion',
  'direccio',
  'gerencia',
  'secretaria',
  'recepcion',
  'recepcio',
  'reception',
  'team',
  'equipo',
  'equip',
  'staff',
  'all',
  'todos',
  'tots',
  'everyone',
  'empresa',
  'company',
  // Systems and support.
  'it',
  'sistemas',
  'sistemes',
  'soporte',
  'suport',
  'support',
  'help',
  'helpdesk',
  'ayuda',
  'ajuda',
  'webmaster',
  'postmaster',
  'hostmaster',
  'abuse',
  'noreply',
  'donotreply',
  'root',
  'security',
  'seguridad',
  'seguretat',
  'privacy',
  'privacidad',
  'privacitat',
  'dpo',
  'legal',
  // Other departments.
  'sales',
  'ventas',
  'vendes',
  'comercial',
  'marketing',
  'billing',
  'facturacion',
  'facturacio',
  'accounts',
  'contabilidad',
  'comptabilitat',
  'compras',
  'compres',
  'finance',
  'finanzas',
  'finances',
  'operations',
  'operaciones',
  'operacions',
  'calidad',
  'qualitat',
  'quality',
  'prensa',
  'premsa',
  'press',
  'comunicacion',
  'comunicacio',
])

/**
 * Words that only say which part of the organization a mailbox belongs to (`dpto.rrhh@`,
 * `area-ventas@`; `team` and `equipo` are mailbox words of their own). With the words of shared
 * mailboxes they make one; next to a name (`dpto.maria@`) they do not.
 */
const UNIT_WORDS: ReadonlySet<string> = new Set([
  'dpto',
  'depto',
  'dept',
  'departamento',
  'departament',
  'department',
  'area',
  'gestion',
  'gestio',
])

/** A numbered mailbox (`rrhh2@`, `info-01@`): one or two digits. An employee number is longer. */
const MAILBOX_NUMBER = /^\d{1,2}$/
const TRAILING_MAILBOX_NUMBER = /(?<!\d)\d{1,2}$/

const isMailboxWord = (word: string) => GENERIC_LOCAL_PARTS.has(word) || UNIT_WORDS.has(word)

/**
 * Whether one word of the address is a shared mailbox's, perhaps numbered (`rrhh2`) or two of
 * them written together (`peopleteam`, `dptorrhh`).
 */
function isMailboxToken(token: string): boolean {
  const word = token.replace(TRAILING_MAILBOX_NUMBER, '')
  if (!word) return false
  if (isMailboxWord(word)) return true
  for (let i = 1; i < word.length; i++)
    if (isMailboxWord(word.slice(0, i)) && isMailboxWord(word.slice(i))) return true
  return false
}

/**
 * Whether an email is a shared mailbox rather than one person's. The part before the @ is read
 * without a `+tag` or accents, and is a shared mailbox when:
 * - written without its dots, hyphens and underscores, it is a mailbox word (`r.r.h.h`,
 *   `recursos.humanos`), perhaps numbered or two of them together (`rrhh01`, `hrteam`); or
 * - each of its words is one, or one or two digits (`dpto.rrhh`, `hr-team`, `info.2`).
 *
 * A person's address that only contains one (`maria.rrhh@`, `hr.12345678@`) is not.
 */
export function isGenericMailbox(email: string): boolean {
  const at = email.lastIndexOf('@')
  if (at <= 0) return false
  const local = email
    .slice(0, at)
    .split('+')[0]!
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
  const words = local.split(/[._-]+/).filter(Boolean)
  if (!words.length) return false
  if (isMailboxToken(words.join(''))) return true
  return (
    words.every((w) => MAILBOX_NUMBER.test(w) || isMailboxToken(w)) &&
    words.some((w) => !MAILBOX_NUMBER.test(w))
  )
}
